import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Row, Col, Modal, Form, message } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import api, { otherApi } from '../api';

const Member = () => {
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [form] = Form.useForm();
  const [data, setData] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await api.get('/get-all-members');
      if (response.data && Array.isArray(response.data.data)) {
        setData(response.data.data);
      } else {
        throw new Error('Unexpected response structure');
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      message.error('Failed to fetch data. Please try again later.');
    }
  };

  const handleAdd = async (values) => {
    try {
      const requestBody = { ...values };
      await otherApi.post('/member/addotherMember', requestBody);
      message.success('Member added successfully');
      fetchData();
      form.resetFields();
      setIsModalVisible(false);
    } catch (error) {
      console.error('Failed to add member:', error.response ? error.response.data : error.message);
      message.error('Failed to add member. Please try again later.');
    }
  };

  const handleUpdate = async (values) => {
    try {
      const apiEndpoint = `/member/updateotherMember/${currentRecord._id}`;
      await otherApi.put(apiEndpoint, values);
      message.success('Member updated successfully');
      fetchData();
      form.resetFields();
      setIsModalVisible(false);
    } catch (error) {
      console.error('Failed to update data:', error.response ? error.response.data : error.message);
      message.error('Failed to update data. Please try again later.');
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (editMode) {
        await handleUpdate(values);
      } else {
        await handleAdd(values);
      }
    } catch (error) {
      message.error('Failed to submit data. Please try again later.');
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleAddMemberClick = () => {
    setEditMode(false);
    setCurrentRecord(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    form.setFieldsValue(record);
    setEditMode(true);
    setCurrentRecord(record);
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this record?',
      onOk: async () => {
        try {
          await otherApi.delete(`/member/deleteMember/${id}`);
          setData(data.filter(item => item._id !== id));
          message.success('Member deleted successfully');
        } catch (error) {
          message.error('Failed to delete member. Please try again later.');
        }
      },
    });
  };

  const handleSearch = () => {
    const filteredData = data.filter(item => item.memberName.toLowerCase().includes(searchText.toLowerCase()));
    setData(filteredData);
  };

  const columns = [
    { title: 'Serial No', dataIndex: 'serialNo', key: 'serialNo' }, // Add Serial No column
    { title: 'ID No', dataIndex: 'idNo', key: 'idNo' },
    { title: 'Member Name', dataIndex: 'memberName', key: 'memberName' },
    { title: 'Father Name', dataIndex: 'fatherName', key: 'fatherName' },
    { title: 'Mobile No', dataIndex: 'mobileNo', key: 'mobileNo' },
    { title: 'Mobile Alternative No', dataIndex: 'mobileAltNo', key: 'mobileAltNo' },
    { title: 'Company Name', dataIndex: 'companyName', key: 'companyName' },
    { title: 'Nature Of Business', dataIndex: 'natureOfBusiness', key: 'natureOfBusiness' },
    { title: 'Business Category', dataIndex: 'businessCategory', key: 'businessCategory' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Address', dataIndex: 'address', key: 'address' },
    { title: 'City 1', dataIndex: 'city1', key: 'city1' },
    { title: 'City 2', dataIndex: 'city2', key: 'city2' },
    { title: 'Village 1', dataIndex: 'village1', key: 'village1' },
    { title: 'Village 2', dataIndex: 'village2', key: 'village2' },
    { title: 'GST No', dataIndex: 'gstNo', key: 'gstNo' },
    { title: 'Aadhar No', dataIndex: 'aadharNo', key: 'aadharNo' },
    { title: 'Marital Status', dataIndex: 'maritalStatus', key: 'maritalStatus' },
    { title: 'Date Of Anniversary', dataIndex: 'anniversaryDate', key: 'anniversaryDate' },
    { title: 'Date Of Birth', dataIndex: 'birthDate', key: 'birthDate' },
    { title: 'Spouse Name', dataIndex: 'spouseName', key: 'spouseName' },
    { title: 'Childrens', dataIndex: 'childrens', key: 'childrens' },
    { title: 'Community Certificate', dataIndex: 'communityCertificate', key: 'communityCertificate' },
    { title: 'Incharge No', dataIndex: 'inchargeNo', key: 'inchargeNo' },
    { title: 'EC Member', dataIndex: 'ecMember', key: 'ecMember' },
    { title: 'Advisory Board Member', dataIndex: 'advisoryBoardMember', key: 'advisoryBoardMember' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button icon={<DeleteOutlined />} onClick={() => handleDelete(record._id)} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={16}>
        <Col span={12}>
          <Input.Search
            placeholder="Search by member name"
            onSearch={handleSearch}
            onChange={(e) => setSearchText(e.target.value)}
            enterButton
          />
        </Col>
        <Col span={12} style={{ textAlign: 'right' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddMemberClick}>
            Add Member
          </Button>
        </Col>
      </Row>
      <Table columns={columns} dataSource={data} rowKey="_id" pagination={{ pageSize: 10 }} />
      <Modal
        title={editMode ? 'Edit Member' : 'Add Member'}
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={editMode ? 'Update' : 'Add'}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="serialNo" label="Serial No">
                <Input />
              </Form.Item>
              <Form.Item name="idNo" label="ID No">
                <Input />
              </Form.Item>
              <Form.Item name="memberName" label="Member Name">
                <Input />
              </Form.Item>
              <Form.Item name="fatherName" label="Father Name">
                <Input />
              </Form.Item>
              <Form.Item name="mobileNo" label="Mobile No">
                <Input />
              </Form.Item>
              <Form.Item name="mobileAltNo" label="Mobile Alternative No">
                <Input />
              </Form.Item>
              <Form.Item name="companyName" label="Company Name">
                <Input />
              </Form.Item>
              <Form.Item name="natureOfBusiness" label="Nature Of Business">
                <Input />
              </Form.Item>
              <Form.Item name="businessCategory" label="Business Category">
                <Input />
              </Form.Item>
              <Form.Item name="email" label="Email">
                <Input />
              </Form.Item>
              <Form.Item name="address" label="Address">
                <Input />
              </Form.Item>
              <Form.Item name="city1" label="City 1">
                <Input />
              </Form.Item>
              <Form.Item name="city2" label="City 2">
                <Input />
              </Form.Item>
              <Form.Item name="village1" label="Village 1">
                <Input />
              </Form.Item>
              <Form.Item name="village2" label="Village 2">
                <Input />
              </Form.Item>
              <Form.Item name="gstNo" label="GST No">
                <Input />
              </Form.Item>
              <Form.Item name="aadharNo" label="Aadhar No">
                <Input />
              </Form.Item>
              <Form.Item name="maritalStatus" label="Marital Status">
                <Input />
              </Form.Item>
              <Form.Item name="anniversaryDate" label="Date Of Anniversary">
                <Input />
              </Form.Item>
              <Form.Item name="birthDate" label="Date Of Birth">
                <Input />
              </Form.Item>
              <Form.Item name="spouseName" label="Spouse Name">
                <Input />
              </Form.Item>
              <Form.Item name="childrens" label="Childrens">
                <Input />
              </Form.Item>
              <Form.Item name="communityCertificate" label="Community Certificate">
                <Input />
              </Form.Item>
              <Form.Item name="inchargeNo" label="Incharge No">
                <Input />
              </Form.Item>
              <Form.Item name="ecMember" label="EC Member">
                <Input />
              </Form.Item>
              <Form.Item name="advisoryBoardMember" label="Advisory Board Member">
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default Member;
